import { useEffect, useMemo, useState } from "react";
import {
  useCorrelationRules,
  useDeleteCorrelationRule,
  useSaveCorrelationRule,
} from "../../hooks/useCorrelationRules";
import { CorrelationRuleList } from "./CorrelationRuleList";
import {
  decodeAll,
  encodeRule,
  type DecodedRule,
} from "./correlationRuleCodec";

interface CorrelationSectionProps {
  integrationId?: number;
}

export function CorrelationSection({ integrationId }: CorrelationSectionProps) {
  const { data: serverData } = useCorrelationRules(integrationId);
  const saveMutation = useSaveCorrelationRule(integrationId);
  const deleteMutation = useDeleteCorrelationRule(integrationId);

  const [rules, setRules] = useState<DecodedRule[]>([]);
  const [hasSeeded, setHasSeeded] = useState(false);

  const decodedFromServer = useMemo<DecodedRule[]>(
    () => (serverData?.rules ? decodeAll(serverData.rules) : []),
    [serverData?.rules],
  );

  useEffect(() => {
    if (!hasSeeded && serverData) {
      setRules(decodedFromServer);
      setHasSeeded(true);
    }
  }, [decodedFromServer, hasSeeded, serverData]);

  const handleSaveRule = async (rule: DecodedRule) => {
    if ("unsupported" in rule) return;
    await saveMutation.mutateAsync(encodeRule(rule));
  };

  const handleDelete = async (ruleId: string) => {
    const rule = rules.find((r) => r.id === ruleId);
    if (rule?.serverRuleId != null) {
      await deleteMutation.mutateAsync(rule.serverRuleId);
    }
  };

  return (
    <div
      className="flex flex-col gap-3"
      style={{
        borderColor: "var(--color-border-neutral-subtlest, #4a4c59)",
      }}
    >
      <CorrelationRuleList
        rules={rules}
        onChange={setRules}
        onSaveRule={handleSaveRule}
        onDelete={handleDelete}
        isDeleting={deleteMutation.isPending}
      />
    </div>
  );
}
